import { DefaultLayout, useArtistContext } from "Components";
import { Card } from "melodies-source/Card";
import { GlobalStyles } from "styled";
import styled from "styled-components";
import { Preview } from "../Components/Preview";
import {
  Dispatch,
  SetStateAction,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { SectionContainer } from "Routes/SetFan/editor/Components/SectionContainer";
import { SvgClipboard } from "melodies-source/Svgs/Clipboard";
import { SvgChecklist } from "melodies-source/Svgs/Checklist";
import { ReactComponent as IconPalette } from "assets/svg/palette.svg";
import { ReactComponent as IconTrophy } from "assets/svg/trophy-large.svg";
import { ReactComponent as IconUserQuestion } from "assets/svg/user-question.svg";
import { ReactComponent as IconMessageCheck } from "assets/svg/message-check.svg";
import { ReactComponent as IconGear } from "assets/svg/gear.svg";
import { SurveyInfoContent, INFO_FIELDS } from "../slices/SurveyInfoContent";
import { Appearance } from "../slices/Appearance";
import { Contest, CONTEST_FIELDS } from "../slices/Contest";
import { SurveyQuestions } from "../slices/SurveyQuestions";
import { ProfilingQuestions } from "../slices/ProfilingQuestions";
import { ThankYouMessage, THANK_YOU_FIELDS } from "../slices/ThankYouMessage";
import {
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
} from "react-router-dom";
import { useSurveyBuilder } from "../hooks/useSurveyBuilder";
import {
  BuilderProvider,
  useBuilderContext,
  useSectionValidation,
} from "../Components/BuilderContext";
import { H2 } from "melodies-source/Text";
import { Button } from "melodies-source/Button";
import { SvgCheck } from "melodies-source/Svgs/Check";
import { PublishWarning } from "../Components/PublishWarning";
import { Prompt } from "contexts/PromptContext";
import { ReactComponent as SaveIcon } from "assets/svg/save-2.svg";
import { useDetectSticky, useMobileMediaQuery } from "hooks";
import { CenterFlexRow } from "Routes/styled";
import { SongsQuestion } from "../Components/SongsQuestion";
import { ContestTitleAddon } from "../slices/ContestTitleAddon";
import { PreviewContainer, PreviewFab } from "../wizard";
import { useUser } from "auth";
import {
  AdditionalSettings,
  ADDITIONAL_SETTINGS_FIELDS,
} from "../slices/AdditionalSettings";
import { ThankYouMessageAddon } from "../slices/ThankYouMessageAddon";
import { ConfirmProvider } from "../Components/ConfirmContext";

const STICKY_OBSERVER_SETTINGS = {
  threshold: [1],
  rootMargin: "-1px 0px 0px 0px",
};

export const Editor = ({
  match,
}: RouteComponentProps<{ surveyId: string }>) => {
  const [page, setPage] = useState<number | null>(0);
  const { survey, version, publishedVersion, isLoading } = useSurveyBuilder(
    match.params.surveyId,
  );

  return (
    <DefaultLayout>
      <GlobalStyles grayBG />
      {survey && version && !isLoading && (
        <BuilderProvider
          survey={survey}
          version={version}
          publishedVersion={publishedVersion}
        >
          <ConfirmProvider>
            <Switch>
              <Route
                path={match.path}
                exact={true}
                render={() => <InnerEditor page={page} setPage={setPage} />}
              />
              <Route
                path={`${match.path}/setlist/:setlistId?`}
                render={() => <InnerSetlist backPath={match.url} />}
              />
            </Switch>
          </ConfirmProvider>
        </BuilderProvider>
      )}
    </DefaultLayout>
  );
};

interface InnerSetlistProps {
  backPath: string;
}

const InnerSetlist = ({ backPath }: InnerSetlistProps) => {
  return (
    <div style={{ padding: 40 }}>
      <SongsQuestion backPath={backPath} />
    </div>
  );
};

interface InnerEditorProps {
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
}

const InnerEditor = ({ page, setPage }: InnerEditorProps) => {
  const { onPublish, publishingStatus, status, data, hasUnpublishedChanges } =
    useBuilderContext();
  const [isPreviewVisible, setIsPreviewVisible] = useState<boolean>(false);
  const isMobile = useMobileMediaQuery();
  const { ref: stickyRef, isSticky } = useDetectSticky(
    undefined,
    STICKY_OBSERVER_SETTINGS,
  );
  const footerObserverSettings = useMemo(
    () => ({ threshold: [1] }),
    [isMobile],
  );
  const { ref: footerRef, isSticky: isFooterFloating } = useDetectSticky(
    undefined,
    footerObserverSettings,
  );
  const { claims } = useUser();
  const history = useHistory();
  const { id: artistGroupId } = useArtistContext();

  // Section validation
  const { hasError: infoHasError } = useSectionValidation(INFO_FIELDS);
  const { hasError: contestHasError } = useSectionValidation(CONTEST_FIELDS);
  const { hasError: thankYouHasError } = useSectionValidation(THANK_YOU_FIELDS);
  const { hasError: additionalSettingsHasError } = useSectionValidation(
    ADDITIONAL_SETTINGS_FIELDS,
  );

  const slices = useMemo(() => {
    const sls = [
      {
        icon: <SvgClipboard />,
        title: "Campaign Info",
        slice: SurveyInfoContent,
        hasError: infoHasError,
      },
      { icon: <IconPalette />, title: "Appearance", slice: Appearance },
      {
        icon: <IconTrophy />,
        title: "Sweepstakes",
        slice: Contest,
        titleAddon: ContestTitleAddon,
        hasError: contestHasError,
      },
      {
        icon: <SvgChecklist />,
        title: "Campaign Questions",
        slice: SurveyQuestions,
      },
      {
        icon: <IconUserQuestion />,
        title: "Profiling Questions",
        slice: ProfilingQuestions,
      },
      {
        icon: <IconMessageCheck />,
        title: "Thank You Message",
        slice: ThankYouMessage,
        requestView: "thankyou",
        titleAddon: ThankYouMessageAddon,
        titleAddonProps: { hideOnMobile: true },
        hasError: thankYouHasError,
      },
    ];
    if (claims?.admin) {
      sls.push({
        icon: <IconGear />,
        title: "Additional settings",
        slice: AdditionalSettings,
        hasError: additionalSettingsHasError,
      });
    }
    return sls;
  }, [
    claims?.admin,
    infoHasError,
    contestHasError,
    thankYouHasError,
    additionalSettingsHasError,
  ]);

  const currentSlice = slices[page];

  const isDraft = status === "draft";
  const isSaved = !hasUnpublishedChanges;
  const showUnpublishedWarning = !isDraft && hasUnpublishedChanges;

  const unpublishedWarning = !isDraft && (
    <WarningSlot
      $visible={showUnpublishedWarning}
      aria-hidden={!showUnpublishedWarning}
    >
      <PublishWarning message="Fans won't see these changes until you publish." />
    </WarningSlot>
  );

  const actions = (
    <Actions>
      {isDraft && (
        <Button
          loading={publishingStatus === "draft"}
          disabled={publishingStatus === "live"}
          variant="tertiary"
          onClick={() => onPublish({ status: "draft" })}
          style={{ minWidth: 156 }}
        >
          Save as Draft
        </Button>
      )}
      {isDraft ? (
        <Button
          style={{ minWidth: 162 }}
          onClick={() => onPublish({ status: "live" })}
          loading={publishingStatus === "live"}
          disabled={publishingStatus === "draft"}
        >
          Publish
        </Button>
      ) : (
        <PublishButton
          $isSaved={isSaved}
          leftIcon={isSaved ? <SvgCheck /> : <SaveIcon />}
          onClick={() => onPublish({ status: "live" })}
          loading={publishingStatus === "live"}
          disabled={isSaved}
        >
          {isSaved ? "Saved" : "Publish Changes"}
        </PublishButton>
      )}
    </Actions>
  );

  useLayoutEffect(() => {
    if (data?.fields.isRestrictedToAdmins.content && !claims.admin) {
      history.replace(`/${artistGroupId}/set-fan/surveys`);
    }
  }, [data?.fields.isRestrictedToAdmins.content, claims.admin]);

  return (
    <EditorGrid>
      <Prompt
        when={showUnpublishedWarning && publishingStatus === "idle"}
        message="Leave without saving?"
        description="You have unsaved changes. If you leave now, they'll be lost."
      />
      <StickyHeader ref={stickyRef} $isSticky={isSticky} isElevated>
        <Title>Edit Campaign</Title>
        {!isMobile && (
          <HeaderActions>
            {unpublishedWarning}
            {actions}
          </HeaderActions>
        )}
      </StickyHeader>
      <Frame>
        <PageContentCard isElevated>
          {slices.map((section, index) => (
            <SectionContainer
              {...section}
              active={index === page}
              onClick={() => setPage((curr) => (curr === index ? null : index))}
              lastIndex={index === slices.length - 1}
              key={`survey-section-${index}`}
              hasError={section.hasError}
            />
          ))}
          {isMobile && (
            <MobileActionsFooter ref={footerRef} $floating={isFooterFloating}>
              {unpublishedWarning}
              {actions}
            </MobileActionsFooter>
          )}
        </PageContentCard>
        {(!isMobile || isPreviewVisible) && (
          <PreviewContainer isVisible={isPreviewVisible}>
            <Preview requestView={currentSlice?.requestView} />
          </PreviewContainer>
        )}
        <PreviewFab
          isVisible={isPreviewVisible}
          onClick={() => setIsPreviewVisible((v) => !v)}
        />
      </Frame>
    </EditorGrid>
  );
};

const Title = styled(H2)`
  color: var(--main-color);
  ${({ theme }) => theme.media.mobile} {
    font-size: 20px;
    line-height: 28px;
  }
`;

export const PageGridContainer = styled.div`
  display: grid;
  grid-template: "title actions" "frame frame" / 1fr 400px;
  align-items: center;
  row-gap: 32px;
  padding: 32px 44px 44px;

  ${({ theme }) => theme.media.mobile} {
    grid-template: "title" "frame" / 1fr;
    padding: 20px 0;
    margin: 0 20px;
    row-gap: 0;
  }
`;

const EditorGrid = styled(PageGridContainer)`
  grid-template: "header" "frame" / 1fr;

  ${({ theme }) => theme.media.mobile} {
    grid-template: "header" "frame" / 1fr;
  }
`;

const StickyHeader = styled(Card)<{ $isSticky: boolean }>`
  grid-area: header;
  position: sticky;
  top: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 15px 20px;
  border-radius: ${({ $isSticky }) => ($isSticky ? "0 0 10px 10px" : "10px")};
  //margin: -32px -44px 0;
  background: #ffffff;
  box-shadow: ${({ $isSticky }) =>
    $isSticky && "0 0 50px 0 rgba(0, 0, 0, 0.15)"};
  transition:
    background-color 200ms ease,
    box-shadow 200ms ease,
    border-radius 200ms ease;

  ${({ theme }) => theme.media.mobile} {
    position: static;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 0;
    margin: 0;
    background: transparent;
    box-shadow: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
  }
`;

const WarningSlot = styled.div<{ $visible: boolean }>`
  display: flex;
  overflow: hidden;
  max-width: ${({ $visible }) => ($visible ? "400px" : "0px")};
  margin-right: ${({ $visible }) => ($visible ? "16px" : "0px")};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateX(${({ $visible }) => ($visible ? "0" : "8px")});
  transition:
    max-width 300ms ease,
    margin-right 300ms ease,
    opacity 200ms ease,
    transform 300ms ease;

  > * {
    white-space: nowrap;
  }

  ${({ theme }) => theme.media.mobile} {
    display: ${({ $visible }) => ($visible ? "flex" : "none")};
    max-width: 100%;
    margin-right: 0;
    font-size: 10px;

    > * {
      white-space: normal;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const PublishButton = styled(Button)<{ $isSaved: boolean }>`
  width: ${({ $isSaved }) => ($isSaved ? "132px" : "220px")};
  min-width: 0;
  overflow: hidden;
  transition: width 300ms ease;

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Actions = styled(CenterFlexRow)`
  grid-area: actions;
  gap: 12px;
  justify-content: flex-end;
  ${({ theme }) => theme.media.mobile} {
    border-top: 1px solid var(--box-shadow-color);
    padding: 24px 20px;
    flex-direction: column-reverse;
    gap: 8px;
    align-items: stretch;
  }
`;

const MobileActionsFooter = styled.div<{ $floating: boolean }>`
  position: sticky;
  bottom: -1px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  background: #ffffff;
  border-top: 1px solid var(--box-shadow-color);
  box-shadow: ${({ $floating }) =>
    $floating ? "0 -12px 24px -12px rgba(0, 0, 0, 0.25)" : "none"};
  border-radius: ${({ $floating }) => ($floating ? "0" : "0 0 12px 12px")};
  transition: all 200ms ease;

  ${Actions} {
    border-top: none;
    padding: 0;
  }
`;

const Frame = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: flex-start;
  grid-area: frame;
  display: grid;
  grid-template: auto / 1fr 400px;

  ${({ theme }) => theme.media.mobile} {
    grid-template: auto / 1fr;
  }
`;

const PageContentCard = styled(Card)`
  display: flex;
  flex-direction: column;
  position: relative;
  box-shadow: 0px 0px 50px rgba(0, 0, 0, 0.15);
  padding: 0;
  border: none;
  margin-right: 44px;
  flex-grow: 1;

  ${({ theme }) => theme.media.mobile} {
    margin-top: 20px;
    margin-right: 0;
    box-shadow: 0px 0px 25px rgba(0, 0, 0, 0.15);
  }
`;
